import { CesiumService } from '@/handlers/cesium.service'
import log from '@/utils/useLogger'
import { Cartesian2, Cartesian3, Cartographic, createWorldTerrainAsync, sampleTerrainMostDetailed } from 'cesium'
import { computed } from 'vue'

// LRU Cache implementation for terrain heights
class LRUCache<K, V> {
  private cache: Map<K, V>
  private maxSize: number

  constructor(maxSize: number) {
    this.cache = new Map()
    this.maxSize = maxSize
  }

  get(key: K): V | undefined {
    if (!this.cache.has(key)) return undefined
    // Move to end (most recently used)
    const value = this.cache.get(key)!
    this.cache.delete(key)
    this.cache.set(key, value)
    return value
  }

  set(key: K, value: V): void {
    if (this.cache.has(key)) {
      this.cache.delete(key)
    } else if (this.cache.size >= this.maxSize) {
      // Remove least recently used (first item)
      const firstKey = this.cache.keys().next().value
      if (firstKey !== undefined) {
        this.cache.delete(firstKey)
      }
    }
    this.cache.set(key, value)
  }

  has(key: K): boolean {
    return this.cache.has(key)
  }
}

const terrainHeightCache = new LRUCache<string, number>(1000)

// Cache the terrain provider instance to avoid recreating it on every request
let cachedTerrainProvider: any = null
let terrainProviderPromise: Promise<any> | null = null

const getOrCreateTerrainProvider = async () => {
  // Return cached instance if available
  if (cachedTerrainProvider) {
    return cachedTerrainProvider
  }

  // If a creation is already in progress, wait for it
  if (terrainProviderPromise) {
    return terrainProviderPromise
  }

  // Create new terrain provider
  terrainProviderPromise = createWorldTerrainAsync()
    .then((provider) => {
      cachedTerrainProvider = provider
      terrainProviderPromise = null
      return provider
    })
    .catch((error) => {
      log.error('Failed to create terrain provider:', error)
      terrainProviderPromise = null
      throw error
    })

  return terrainProviderPromise
}

export const useMapFunctions = () => {
  const viewer = computed(() => CesiumService.viewer.value)
  const pickValidPosition = (windowPosition: Cartesian2): Cartesian3 | undefined => {
    try {
      // First try scene.pickPosition (most accurate with 3D terrain)
      let position = viewer.value?.scene.pickPosition(windowPosition)
      // Check if the position is valid
      if (isValidPosition(position)) {
        return position
      }
      // Fallback to globe.pick
      const ray = viewer.value?.camera.getPickRay(windowPosition)
      if (ray) {
        const globePosition = viewer.value?.scene.globe.pick(ray, viewer.value?.scene)
        if (isValidPosition(globePosition)) {
          return globePosition
        }
      }
    } catch (err) {
      log.error('error picking valid position', err)
    }
  }

  const isValidPosition = (position: Cartesian3 | undefined): boolean => {
    return position !== undefined && !isNaN(position.x) && !isNaN(position.y) && !isNaN(position.z)
  }
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    // Earth radius in meters
    const R = 6371000

    const dLat = ((lat2 - lat1) * Math.PI) / 180
    const dLon = ((lon2 - lon1) * Math.PI) / 180

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2)

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    const distance = R * c

    return distance
  }
  const flyToLocation = (
    longitude: number,
    latitude: number,
    height: number,
    altitude?: number,
  ) => {
    if (!viewer.value) return
    const position = Cartesian3.fromDegrees(
      Number(longitude),
      Number(latitude),
      Number(height) + (Number(altitude) || 100),
    )
    viewer.value.camera.flyTo({
      destination: position,
      duration: 1.0,
    })
  }

  /**
   * Race a promise against an AbortSignal. When the signal aborts, the returned
   * promise rejects with AbortError so callers can bail out of their async flow.
   * The underlying promise still resolves (Cesium's terrain sampling has no abort
   * API), but its result is ignored.
   */
  const raceAbort = <T>(p: Promise<T>, signal?: AbortSignal): Promise<T> => {
    if (!signal) return p
    return new Promise<T>((resolve, reject) => {
      if (signal.aborted) {
        reject(new DOMException('Aborted', 'AbortError'))
        return
      }
      const onAbort = () => {
        signal.removeEventListener('abort', onAbort)
        reject(new DOMException('Aborted', 'AbortError'))
      }
      signal.addEventListener('abort', onAbort, { once: true })
      p.then(
        (v) => {
          signal.removeEventListener('abort', onAbort)
          resolve(v)
        },
        (e) => {
          signal.removeEventListener('abort', onAbort)
          reject(e)
        },
      )
    })
  }

  const isAbortError = (err: unknown): boolean =>
    err instanceof DOMException && err.name === 'AbortError'

  interface TerrainOpts {
    signal?: AbortSignal
    precision?: number
  }

  /**
   * Get terrain height for a single location
   * Uses cache and reuses terrain provider for performance.
   * Accepts optional AbortSignal for cooperative cancellation.
   */
  const getTerrainHeight = async (
    longitude: number,
    latitude: number,
    opts: TerrainOpts = {},
  ) => {
    const { signal, precision = 7 } = opts
    const key = `${Number(longitude).toFixed(precision)},${Number(latitude).toFixed(precision)}`
    if (terrainHeightCache.has(key)) {
      return terrainHeightCache.get(key)!
    }

    try {
      const terrainProvider = await raceAbort(getOrCreateTerrainProvider(), signal)
      if (!terrainProvider) return 104

      const position = Cartographic.fromDegrees(longitude, latitude)
      const positions = [position]

      const updatedPositions = await raceAbort(
        sampleTerrainMostDetailed(terrainProvider, positions),
        signal,
      )
      const rawHeight = updatedPositions[0].height
      const height = Number.isFinite(rawHeight) ? rawHeight : 104
      terrainHeightCache.set(key, height)
      return height
    } catch (error) {
      if (isAbortError(error)) throw error
      log.error('Error sampling terrain:', error)
      return 104
    }
  }

  /**
   * Batch get terrain heights for multiple locations in a single API call
   * Much more efficient than calling getTerrainHeight multiple times.
   * Accepts optional AbortSignal for cooperative cancellation.
   * @param locations Array of {longitude, latitude} objects
   * @returns Array of heights in same order as input
   */
  const getBatchTerrainHeights = async (
    locations: Array<{ longitude: number; latitude: number }>,
    opts: TerrainOpts = {},
  ) => {
    const { signal, precision = 7 } = opts
    try {
      const results: number[] = new Array(locations.length)
      const uncachedIndices: number[] = []
      const uncachedPositions: any[] = []

      locations.forEach((loc, index) => {
        const key = `${Number(loc.longitude).toFixed(precision)},${Number(loc.latitude).toFixed(precision)}`
        if (terrainHeightCache.has(key)) {
          results[index] = terrainHeightCache.get(key)!
        } else {
          uncachedIndices.push(index)
          uncachedPositions.push(Cartographic.fromDegrees(loc.longitude, loc.latitude))
        }
      })

      if (uncachedPositions.length === 0) {
        return results
      }

      const terrainProvider = await raceAbort(getOrCreateTerrainProvider(), signal)
      if (!terrainProvider) {
        uncachedIndices.forEach((index) => (results[index] = 104))
        return results
      }

      const updatedPositions = await raceAbort(
        sampleTerrainMostDetailed(terrainProvider, uncachedPositions),
        signal,
      )

      updatedPositions.forEach((position, i) => {
        const originalIndex = uncachedIndices[i]
        const loc = locations[originalIndex]
        const height = Number.isFinite(position.height) ? position.height : 104
        const key = `${Number(loc.longitude).toFixed(precision)},${Number(loc.latitude).toFixed(precision)}`
        terrainHeightCache.set(key, height)
        results[originalIndex] = height
      })

      return results
    } catch (error) {
      if (isAbortError(error)) throw error
      log.error('Error batch sampling terrain:', error)
      return locations.map(() => 104)
    }
  }
  const removeEntityIfExists = (id: string | null) => {
    if (!viewer.value || !id) return

    try {
      const existingEntity = viewer.value.entities.getById(id)
      if (existingEntity) {
        viewer.value.entities.remove(existingEntity)
      }
    } catch (error) {
      log.error(`Error removing entity with id ${id}:`, error)
    }
  }
  const getWaypointInfo = async (
    longitude: number | string,
    latitude: number | string,
    altitude: number | string,
  ) => {
    if (!viewer.value) return
    // Convert inputs to numbers
    const numLongitude = Number(longitude)
    const numLatitude = Number(latitude)
    const numAltitude = Number(altitude)

    // Validate the converted numbers
    if (isNaN(numLongitude) || isNaN(numLatitude) || isNaN(numAltitude)) {
      log.error('Invalid coordinates or altitude provided to getWaypointInfo')
      throw new Error('Invalid coordinates or altitude')
    }

    const terrainHeight = await getTerrainHeight(numLongitude, numLatitude)
    const groundPosition = Cartesian3.fromDegrees(numLongitude, numLatitude, terrainHeight)
    const topPosition = Cartesian3.fromDegrees(
      numLongitude,
      numLatitude,
      numAltitude + terrainHeight,
    )
    return {
      groundPosition,
      topPosition,
      terrainHeight,
    }
  }
  return {
    pickValidPosition,
    calculateDistance,
    getTerrainHeight,
    getBatchTerrainHeights,
    getWaypointInfo,
    flyToLocation,
    removeEntityIfExists,
  }
}
